// Button — 1:1 port of legacy `src/ui/Primitives.jsx:6–35`.
// Props, defaults, inline style object preserved verbatim.
import { forwardRef, type CSSProperties, type MouseEventHandler, type ReactNode } from 'react';
import { Icon, type IconName } from './Icon.js';

export interface ButtonProps {
  kind?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'quiet';
  size?: 'sm' | 'md';
  icon?: IconName;
  trailingIcon?: IconName;
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  children?: ReactNode;
  style?: CSSProperties;
  type?: 'button' | 'submit' | 'reset';
  form?: string;
  active?: boolean;
  /** Forwarded to the rendered <button> for screen-reader announcements. */
  'aria-label'?: string;
  /** Forwarded to the rendered <button>; useful for live-region pairing. */
  'aria-describedby'?: string;
  /** Forwarded to the rendered <button>; announces collapsed/expanded state. */
  'aria-expanded'?: boolean;
  /** Forwarded to the rendered <button>; points at the controlled region. */
  'aria-controls'?: string;
  /** Forwarded to the rendered <button>; used for stable test selectors. */
  'data-testid'?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    kind = 'secondary',
    size = 'md',
    icon,
    trailingIcon,
    disabled,
    onClick,
    children,
    style,
    type = 'button',
    form,
    active,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    'aria-expanded': ariaExpanded,
    'aria-controls': ariaControls,
    'data-testid': dataTestid,
  },
  ref,
) {
  const h = size === 'sm' ? 'var(--control-h-sm)' : 'var(--control-h)';
  const pad = size === 'sm' ? '0 10px' : '0 14px';
  const base: CSSProperties = {
    height: h,
    padding: icon || trailingIcon ? (size === 'sm' ? '0 8px' : '0 12px') : pad,
    borderRadius: 'var(--r-2)',
    fontFamily: 'var(--font-ui)',
    fontSize: size === 'sm' ? 12 : 13,
    fontWeight: 500,
    border: '1px solid transparent',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
    transition:
      'background var(--dur-1) var(--ease-out), color var(--dur-1) var(--ease-out), border-color var(--dur-1) var(--ease-out)',
  };
  const kinds: Record<NonNullable<ButtonProps['kind']>, CSSProperties> = {
    primary: { background: 'var(--accent-1)', color: 'var(--fg-invert)' },
    secondary: { background: 'var(--bg-1)', color: 'var(--fg-1)', borderColor: 'var(--border-1)' },
    ghost: { background: 'transparent', color: 'var(--fg-1)' },
    danger: {
      background: 'var(--bg-1)',
      color: 'var(--status-error-fg)',
      borderColor: 'var(--border-1)',
    },
    quiet: { background: 'transparent', color: 'var(--fg-2)' },
  };
  const activeStyle: CSSProperties | null = active
    ? {
        background: 'var(--accent-1-subtle)',
        color: 'var(--accent-1-press)',
        borderColor: 'transparent',
      }
    : null;
  return (
    <button
      ref={ref}
      type={type}
      form={form}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      data-testid={dataTestid}
      style={{ ...base, ...kinds[kind], ...(activeStyle ?? {}), ...style }}
    >
      {icon && <Icon name={icon} size={size === 'sm' ? 14 : 15} />}
      {children}
      {trailingIcon && <Icon name={trailingIcon} size={size === 'sm' ? 14 : 15} />}
    </button>
  );
});
